import React, { useMemo } from "react";
import DatePicker from "react-datepicker";

import { MasterSelect } from "../../common/MasterSelect";

import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import actions from "@/actions";
import { useSearchInput } from "@/components/comment/hooks/useSearchInput";
import { FormGroup, FormRow, Spacer } from "@/components/common";
import {
  FilterConfigGroup,
  SearchConditionConfigModal,
  SearchConditionDisplayButton,
} from "@/components/common/SearchCondition";
import { SettingIconButton } from "@/components/common/SettingIconButton";
import { useSearchConditionConfig } from "@/hooks/useSearchConditionConfig";
import { CommentSearchKey, CommentSearchKeyType, CommentSearchParams } from "@/models/comments";
import { FilterConfig } from "@/models/filterConfig";
import { ScreenName, SearchConditionConfigDefinition } from "@/models/screenConfig";
import { RootState } from "@/reducers/types";

import _ from "lodash";
import format from "date-fns/format";

type Props = {
  open: boolean;
  onReset: () => void;
  onSearch: () => void;
  setPage: (page: number) => void;
};

export const CommentSearchArea = (props: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // 検索条件設定
  const commentSearchConditionDefinitions = useMemo<SearchConditionConfigDefinition<CommentSearchKeyType>[]>(
    () => [
      {
        itemsPerLine: 4,
        items: [
          { key: CommentSearchKey.Category, label: t("machines_category") },
          { key: CommentSearchKey.Comment, label: t("comment") },
          { key: CommentSearchKey.InsertDate, label: t("registered_day") },
        ],
      },
    ],
    []
  );
  const config = useSearchConditionConfig<CommentSearchKeyType>({
    screenName: ScreenName.COMMENT,
    requiredKeys: commentSearchConditionDefinitions.flatMap((v) =>
      v.items.filter((item) => item.required).map((item) => item.key)
    ),
  });
  const filterSearchConditionByConfig = (params: CommentSearchParams): CommentSearchParams => {
    if (!config.map || config.showAllConditions) return params;

    return {
      category_id: config.map.get(CommentSearchKey.Category) ? params.category_id : [],
      comment: config.map.get(CommentSearchKey.Comment) ? params.comment : undefined,
      insert_start_date: config.map.get(CommentSearchKey.InsertDate) ? params.insert_start_date : undefined,
      insert_end_date: config.map.get(CommentSearchKey.InsertDate) ? params.insert_end_date : undefined,
    };
  };

  // 検索条件
  const searchInput = useSearchInput({ filterSearchConditionByConfig, setPage: props.setPage });
  const categories = useSelector((state: RootState) => state.construction?.masters?.categories);

  // 検索条件保存
  const getCurrentConditionLabels = () => {
    const p = searchInput.searchCondition();
    // 現在の条件を保存モーダルに表示するために編集する
    const formatDate = (d: string | null): string => (d ? format(new Date(d), "yyyy/MM/dd") : "");
    const names: { label: string; values: string[] | null }[] = [
      {
        label: t("machines_category"),
        values: p.category_id?.map((categoryId) => categories.find((v) => v.category_id === categoryId)?.category_name),
      },
      { label: t("comment"), values: [p.comment] },
      {
        label: t("registered_day"),
        values: [
          p.insert_end_date || p.insert_end_date
            ? `${formatDate(p.insert_start_date)}〜${formatDate(p.insert_end_date)}`
            : "",
        ],
      },
    ];

    return names.map((n) => ({ label: n.label, values: _.compact(n.values) })).filter((n) => n.values?.length);
  };
  const currentSearchParams = useMemo<CommentSearchParams>(() => {
    return searchInput.searchCondition();
  }, [searchInput.searchCondition]);
  const handleApplyFilterConfig = (filter: FilterConfig<CommentSearchParams>) => {
    // 検索条件設定で表示している検索条件のみ復元する
    const condition = filterSearchConditionByConfig(filter.config);
    searchInput.setCategoryIds(condition.category_id ?? []);
    searchInput.setComment(condition.comment ?? "");
    searchInput.setInsertStartDate(condition.insert_start_date ? new Date(condition.insert_start_date) : null);
    searchInput.setInsertEndDate(condition.insert_end_date ? new Date(condition.insert_end_date) : null);
  };

  if (!props.open) return <></>;

  return (
    <>
      <div id="comment-search-config-area" className="flex justify-between bg-[#e4e4e4] w-[1200px]">
        <FilterConfigGroup
          screenName={ScreenName.COMMENT}
          getCurrentConditionLabels={getCurrentConditionLabels}
          currentConfig={currentSearchParams}
          applyFilterConfig={handleApplyFilterConfig}
        />
        <SettingIconButton className="w-[32px] p-[8px] " onClick={config.openSettingModal} />
      </div>
      <div id="comment-search-area" className="search-box grid gap-y-[16px]">
        <FormRow>
          <FormGroup
            title={t("machines_category")}
            className="w-360"
            display={config.show([CommentSearchKey.Category])}
          >
            <MasterSelect
              prefix="category"
              isMulti={true}
              options={categories ?? []}
              value={searchInput.categoryIds}
              onChange={searchInput.setCategoryIds}
            />
          </FormGroup>
          <FormGroup
            title={t("comment")}
            className="w-330"
            formClassName="relative overflow-visible"
            display={config.show([CommentSearchKey.Comment])}
          >
            <input
              data-test-id="text-import-file-name"
              type="text"
              className="form-control w-230"
              value={searchInput.comment}
              onChange={(e) => searchInput.setComment(e.target.value)}
            />
            {searchInput.error?.comment && (
              <div className="form-error-message absolute top-[23px] left-0 whitespace-nowrap">
                {searchInput.error.comment}
              </div>
            )}
          </FormGroup>
        </FormRow>
        <FormRow display={config.show([CommentSearchKey.InsertDate])}>
          <FormGroup title={t("registered_day")} formClassName="relative overflow-visible">
            <div className="d-ib">
              <DatePicker
                wrapperClassName="w-120"
                disabled={false}
                selected={searchInput.insertStartDate}
                dateFormat="yyyy/MM/dd"
                locale={t("calender_locale")}
                onChange={searchInput.setInsertStartDate}
              />
            </div>
            <div className="d-ib w-30 ta-c">&nbsp;〜&nbsp;</div>
            <div className="d-ib">
              <DatePicker
                wrapperClassName="w-120"
                disabled={false}
                selected={searchInput.insertEndDate}
                dateFormat="yyyy/MM/dd"
                locale={t("calender_locale")}
                onChange={searchInput.setInsertEndDate}
              />
            </div>
            {searchInput.error?.date && (
              <p className="form-error-message absolute top-[23px] left-0 whitespace-nowrap">
                {searchInput.error.date}
              </p>
            )}
          </FormGroup>
        </FormRow>
        <FormRow className="justify-between">
          <Spacer x="210px" />
          <SearchConditionDisplayButton
            isExpanded={config.showAllConditions}
            onChange={config.toggleDisplayOptionalConditions}
          />
          <div className="form-group btn-area pull-right">
            <button
              data-test-id="button-import-reset"
              className="btn btn-gray"
              onClick={() => {
                searchInput.clear();
                props.onReset();
                dispatch(actions.filterConfig.clearSelectedFilterConfig());
              }}
            >
              {t("reset")}
            </button>
            <button
              data-test-id="button-import-search"
              className="btn btn-blue"
              onClick={() => {
                searchInput.search();
                props.onSearch();
              }}
            >
              {t("search")}
            </button>
          </div>
        </FormRow>
      </div>
      {config.showSettingModal && (
        <SearchConditionConfigModal<CommentSearchKeyType>
          title={t("comment") + t("search_condition_config")}
          configMap={config.map}
          definitions={commentSearchConditionDefinitions}
          onClose={config.closeSettingModal}
          onSubmit={(c) => config.saveSearchItemConfig(c)}
        />
      )}
    </>
  );
};
